/**
 * TonightPage.tsx
 * Route: /tonight
 *
 * Shows everything happening TODAY grouped by time slot.
 * The answer to "what's good tonight?" at 2pm on a Friday.
 *
 * FIX: VenueTonightCard now checks isVenueOpenForBusiness before
 * showing "Live Now" — all-day deals no longer show as live before
 * the bar actually opens.
 */

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAppContext } from '../contexts/AppContext'
import { isVenueActiveNow, getEffectiveVenueStatus, isVenueOpenForBusiness, fmtTime } from '../utils/filters'
import { getVenueStatus, STATUS_VISUALS } from '../utils/happeningNow'
import { DEAL_TYPE_COLORS, DEAL_TYPE_LABELS } from '../types'
import { Analytics, track } from '../services/analytics'
import { addToCalendar as addToCalendarShared, isIOSDevice } from '../utils/calendarEvent'
import { openExternal } from '../utils/openExternal'
import type { Venue, HappyHourSchedule } from '../types'
import { JS_DAY_TO_DOW } from '../types'

import { fmtDealValue } from '../utils/dealFormat'

const DEAL_ORDER = ['beer', 'cocktail', 'food', 'wine', 'general']

interface TimeSlot {
  label:      string
  startMins:  number
  venues:     Array<{ venue: Venue; schedule: HappyHourSchedule }>
}

function toMins(t: string): number {
  const [h, m] = t.split(':').map(Number)
  return h * 60 + m
}

function groupByTimeSlot(venues: Venue[]): TimeSlot[] {
  const today = JS_DAY_TO_DOW[new Date().getDay()]

  const pairs: Array<{ venue: Venue; schedule: HappyHourSchedule; startMins: number }> = []
  venues.forEach(venue => {
    // A schedule can say "this runs today" while the venue's actual
    // business hours say the doors are shut today (stale/conflicting
    // data). Previously that conflict only showed up as a mismatched
    // "Not today" badge sitting above fully-rendered, seemingly-live
    // deal pills — confusing since the two signals contradicted each
    // other. Excluding here means the badge and the listing agree.
    if (getEffectiveVenueStatus(venue).status === 'not_today') return
    ;(venue.schedules ?? []).forEach(s => {
      if (!s.days.includes(today)) return
      pairs.push({ venue, schedule: s, startMins: s.is_all_day ? 0 : toMins(s.start_time) })
    })
  })

  pairs.sort((a, b) => a.startMins - b.startMins)

  const slotMap = new Map<string, TimeSlot>()
  pairs.forEach(({ venue, schedule, startMins }) => {
    const timeKey = schedule.is_all_day ? 'All day' : fmtTime(schedule.start_time)
    if (!slotMap.has(timeKey)) {
      slotMap.set(timeKey, { label: timeKey, startMins, venues: [] })
    }
    slotMap.get(timeKey)!.venues.push({ venue, schedule })
  })

  return Array.from(slotMap.values())
}

function VenueTonightCard({ venue, schedule }: { venue: Venue; schedule: HappyHourSchedule }) {
  // Check if the venue is physically open for business right now
  const openForBusiness = isVenueOpenForBusiness(venue)

  const rawStatus = getEffectiveVenueStatus(venue)

  // If the bar isn't open yet (e.g. all-day deal but bar opens at 11am),
  // downgrade live_now / ends_soon to later_today so the badge is accurate
  const effectiveStatus =
    !openForBusiness && (rawStatus.status === 'live_now' || rawStatus.status === 'ends_soon')
      ? { ...rawStatus, status: 'later_today' as const, badge: 'Later today' }
      : rawStatus

  const vis    = STATUS_VISUALS[effectiveStatus.status] ?? STATUS_VISUALS['later_today']
  const isOpen = isVenueActiveNow(venue)  // respects business hours

  const sortedDeals = [...(schedule.deals ?? [])]
    .sort((a, b) => DEAL_ORDER.indexOf(a.type) - DEAL_ORDER.indexOf(b.type))
    .slice(0, 3)

  function handleCalendar(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (!schedule.start_time || !schedule.end_time) return
    addToCalendarShared(venue, schedule, openExternal)
    track('add_to_calendar', { venue_id: venue.id, venue_name: venue.name, platform: isIOSDevice() ? 'ios' : 'other' })
  }

  return (
    <Link to={`/venue/${venue.id}`} className="tonight-card" style={{ textDecoration: 'none' }}>
      {/* Left accent stripe — green when live */}
      <div className="tonight-card-left">
        {isOpen && <div className="tonight-live-stripe" />}
      </div>

      <div className="tonight-card-body">
        {/* Top row: name + status badge */}
        <div className="tonight-card-top">
          <div style={{ minWidth: 0 }}>
            <div className="tonight-card-name">{venue.name}</div>
            <div className="tonight-card-meta">{venue.neighborhood}</div>
          </div>
          {effectiveStatus.badge && (
            <span
              className="tonight-card-badge"
              style={{ background: vis.bg, color: vis.text }}
            >
              {(effectiveStatus.status === 'live_now' || effectiveStatus.status === 'ends_soon') && (
                <span
                  className={`tonight-badge-dot${vis.pulse ? ' pulse' : ''}`}
                  style={{ background: vis.dot }}
                />
              )}
              {effectiveStatus.badge}
            </span>
          )}
        </div>

        {/* Time */}
        <div className="tonight-card-time">
          {schedule.is_all_day ? 'All day' : `${fmtTime(schedule.start_time)} – ${fmtTime(schedule.end_time)}`}
        </div>

        {/* Deal pills */}
        {sortedDeals.length > 0 && (
          <div className="tonight-pills">
            {sortedDeals.map((deal, i) => {
              const colors = DEAL_TYPE_COLORS[deal.type as keyof typeof DEAL_TYPE_COLORS]
              return (
                <span
                  key={i}
                  className="tonight-pill"
                  style={{ background: colors?.bg, color: colors?.text }}
                >
                  <span className="tonight-pill-type">
                    {DEAL_TYPE_LABELS[deal.type as keyof typeof DEAL_TYPE_LABELS]}
                  </span>
                  {(() => {
                    const label = fmtDealValue(deal as any)
                    return label
                      ? <span className="tonight-pill-price">{label}</span>
                      : <span>{deal.description.length > 18 ? deal.description.slice(0, 18) + '…' : deal.description}</span>
                  })()}
                </span>
              )
            })}
          </div>
        )}

        {/* Calendar button */}
        {!schedule.is_all_day && (
          <button className="tonight-cal-btn" onClick={handleCalendar}>
            📅 Add to Calendar
          </button>
        )}
      </div>
    </Link>
  )
}

export default function TonightPage() {
  const { venues, loading, city } = useAppContext()
  const [, setTick] = useState(0)

  useEffect(() => {
    Analytics.seoPageViewed('tonight', city)
    Analytics.pageVisited('tonight')
    const id = setInterval(() => setTick(t => t + 1), 60_000)
    return () => clearInterval(id)
  }, [city])

  const today     = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
  const slots     = groupByTimeSlot(venues)
  const totalVenues = new Set(slots.flatMap(s => s.venues.map(v => v.venue.id))).size
  const liveCount = venues.filter(v => isVenueActiveNow(v)).length

  return (
    <>
      <Helmet>
        <title>Happy Hour Tonight in {city} — What's On Today</title>
        <meta name="description" content={`Everything happening tonight for happy hour in ${city}. Sorted by start time so you can plan your evening.`} />
      </Helmet>

      <div className="tonight-page">

        {/* ── HERO ── */}
        <div className="tonight-hero">
          <div className="tonight-hero-date">{today}</div>
          <h1 className="tonight-hero-title">Tonight in {city}</h1>
          <p className="tonight-hero-sub">
            {totalVenues > 0
              ? `${totalVenues} venues · ${liveCount > 0 ? `${liveCount} open right now` : 'sorted by start time'}`
              : 'No happy hours scheduled today'
            }
          </p>
          <div className="tonight-hero-actions">
            <Link to="/now" className="tonight-hero-live-btn">
              <span className="tonight-hero-dot" />
              {liveCount} live right now
            </Link>
            <Link to="/crawl" className="tonight-hero-crawl-btn">🍺 Build a crawl</Link>
          </div>
        </div>

        {loading && <p className="loading-msg">Loading...</p>}

        {!loading && slots.length === 0 && (
          <div className="hn-empty">
            <div className="hn-empty-icon">🌙</div>
            <div className="hn-empty-title">Nothing scheduled today</div>
            <div className="hn-empty-sub">Check back on a weekday — happy hours typically run Mon–Fri.</div>
            <Link to="/" className="hn-empty-btn">Browse all venues</Link>
          </div>
        )}

        {/* ── TIME SLOTS ── */}
        {!loading && slots.map(slot => (
          <div key={slot.label} className="tonight-slot">
            <div className="tonight-slot-header">
              <span className="tonight-slot-time">{slot.label}</span>
              <span className="tonight-slot-count">
                {slot.venues.length} {slot.venues.length === 1 ? 'venue' : 'venues'}
              </span>
            </div>
            {slot.venues.map(({ venue, schedule }) => (
              <VenueTonightCard key={`${venue.id}-${schedule.id}`} venue={venue} schedule={schedule} />
            ))}
          </div>
        ))}

      </div>
    </>
  )
}
